/* eslint-disable @typescript-eslint/no-explicit-any */
import { SourceType } from '@aon/tws-common-ui-questions/build/lib/models';
import axios, { CancelToken } from 'axios';
import Constants from '../constants';
import {
	IAudienceSourceVm,
	IJsonPatchDocument,
	ILibraryQuestion,
	IQuestion,
	ISummaryItem,
	ISurvey,
	ISurveyAdminSummary,
	ISurveyEditModel,
	ISurveyListItem,
	ISurveyPreviewModel,
	ISurveyStatusLog,
	IValidationModel,
	SurveySummaryItemTypeId,
} from '../models';
import { ISurveyBridge } from '../models/ISurveyBridge';
import { ISurveyTemplate } from '../models/ISurveyTemplate';
import { SurveyStatusType } from '../models/SurveyStatusType';
import { APIHelper } from '../utilities/APIHelper';

export async function addSurvey(surveyEditModel: ISurveyEditModel): Promise<IValidationModel<ISurveyEditModel>> {
	const res = await axios.post(Constants.API_SURVEY_EDIT_MODEL_URL, surveyEditModel).catch(APIHelper.handleValidationError);
	return res.data;
}

export async function cloneSurvey(surveyId: number): Promise<ISurveyListItem> {
	try {
		const res = await axios.get(Constants.API_SURVEY_LIST_URL + '/' + surveyId + '/clone', { headers: { Pragma: 'no-cache' } });
		if (res.status === 200) {
			return res.data;
		}
	} catch {
		/*generic error*/
	}
	throw new Error('An error occurred trying to clone the survey.');
}

export async function bulkCloneSurvey(surveyId: number, userIds: number[]): Promise<ISurveyListItem[]> {
	const res = await axios.put(Constants.API_SURVEY_LIST_URL + '/' + surveyId + '/bulk-clone', userIds);
	return res.data;
}

export async function deleteSurvey(surveyId: number) {
	try {
		const res = await axios.delete(Constants.API_SURVEY_LIST_URL + '/' + surveyId, { headers: { Pragma: 'no-cache' } });
		return res.data;
	} catch {
		/* generic error */
	}
	throw new Error('An error occurred trying to delete the survey.');
}

export async function publishSurvey(surveyId: number, preview?: boolean) {
	let url = Constants.API_SURVEYDEFINITIONS_URL + '/' + surveyId + '/?surveyDefinitionAction=PublishToMResearch';
	if (preview === true) {
		url += '&isPreviewSurvey=true';
	}
	const res = await axios.post(url);
	return res.data;
}

export async function saveSurvey(surveyEditModel: ISurveyEditModel, surveyLogo?: any): Promise<IValidationModel<ISurveyEditModel>> {
	const data = new FormData();
	data.append('file', surveyLogo);
	data.append('surveyEditModel', JSON.stringify(surveyEditModel));
	const url = Constants.API_SURVEY_EDIT_MODEL_URL + '/' + surveyEditModel.survey.surveyId;
	const res = await axios.put(url, data, { headers: { Pragma: 'no-cache' } }).catch(APIHelper.handleValidationError);
	return res.data;
}

export async function UpdateSurveyStatus(surveyId: number, statusType: SurveyStatusType) {
	const surveyPatch: IJsonPatchDocument[] = [
		{
			op: 'replace',
			path: 'surveyStatus',
			value: statusType,
		},
	];
	const res = await axios.patch(getUpdateSurveyStatusUrl(surveyId), surveyPatch).catch((error) => {
		if (error.response.status === 422) {
			return error.response;
		}
		throw Error('An error occurred trying to save a custom question.');
	});
	return res.data;
}

type JsonPatchDocumentOperation = 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';

type JsonPatchDocument<TObject, TPath = keyof TObject, TValue = TObject[keyof TObject]> = {
	op: JsonPatchDocumentOperation;
	path: TPath;
	value: TValue;
};

type JsonPatchDocumentData<TObject> = { [key in keyof TObject]: TObject[key] };

const createPatchDocument = <TObject>(
	data: JsonPatchDocumentData<TObject>,
	operation: JsonPatchDocumentOperation = 'replace',
): JsonPatchDocument<TObject>[] =>
	Object.entries(data).map(([key, value]) => ({
		op: operation,
		path: key as keyof TObject,
		value: value as TObject[keyof TObject],
	}));

export const patchSurvey = async (surveyId: number, data: { [key in keyof ISurvey]?: ISurvey[key] }) => {
	const surveyPatchDocument = createPatchDocument(data);
	const response = await axios.patch<ISurvey>(`${Constants.SURVEY_BUILDER_SURVEYS_API_URL}/${surveyId}`, surveyPatchDocument);
	return response.data;
};

export function getSurveysQuery(searchText: string) {
	let params = Constants.API_SURVEY_LIST_URL;
	if (searchText) {
		params += '?searchText=' + encodeURIComponent(searchText);
	}
	return params;
}

export async function getSurveys(searchText: string): Promise<ISurveyListItem[]> {
	try {
		const res = await axios.get(getSurveysQuery(searchText), { headers: { Pragma: 'no-cache' } });
		if (res.status === 200) {
			return res.data;
		}
	} catch {
		/*generic error*/
	}
	throw new Error('An error occurred while trying to fetch surveys.');
}

export async function getSurvey(surveyId: number): Promise<IValidationModel<ISurveyEditModel>> {
	const res = await axios.get(Constants.API_SURVEY_EDIT_MODEL_URL + '/' + surveyId, { headers: { Pragma: 'no-cache' } });
	return res.data;
}

export async function getSurveySummaryItems(
	surveyId: number,
	options: {
		ignoreValidations?: boolean;
		includeUnsupportedLanguageWarning?: boolean;
		summaryItemTypes?: number[];
		cancellationToken?: CancelToken;
	} = { ignoreValidations: false, includeUnsupportedLanguageWarning: false },
): Promise<ISummaryItem[]> {
	const { ignoreValidations = false, includeUnsupportedLanguageWarning = false, summaryItemTypes, cancellationToken } = options;

	const url = new URL(`${Constants.API_SURVEYSUMMARYITEMS_URL}/${surveyId}`);

	if (ignoreValidations) {
		url.searchParams.set('ignoreValidations', ignoreValidations.toString());
	}

	if (includeUnsupportedLanguageWarning) {
		url.searchParams.set('includeUnsupportedLanguageWarning', includeUnsupportedLanguageWarning.toString());
	}

	if (summaryItemTypes !== undefined) {
		summaryItemTypes.forEach((summaryItemType) => url.searchParams.append('summaryItemTypes', summaryItemType.toString()));
	}

	const res = await axios.get<ISummaryItem[]>(url.href, {
		headers: { Pragma: 'no-cache' },
		cancelToken: cancellationToken,
	});

	return res.data;
}

// This is a strange way to get questions from a survey, but the alternative is to
// return either the entire export model or the edit model. We don't have a survey
// questions resource in the api.
export async function getSurveyQuestions(surveyId: number): Promise<ILibraryQuestion[]> {
	const data = await getSurveySummaryItems(surveyId, { ignoreValidations: true, summaryItemTypes: [SurveySummaryItemTypeId.Questions] });
	const questions = extractQuestionsFromSummary(data);
	return convertSummaryQuestionsToLibraryQuestions(questions);
}

export async function getSurveyAdminSummaryViewModel(surveyId: number): Promise<ISurveyAdminSummary> {
	try {
		const res = await axios.get(Constants.API_SURVEYADMINSUMMARIES_URL + '/' + surveyId);
		if (res.status === 200) {
			return res.data;
		}
	} catch {
		// fall through
	}
	throw new Error('An error occurred while trying to fetch Survey Admin Summary.');
}

export const getSurveyBenchmarksUrl = (surveyId: number) => Constants.SURVEY_BUILDER_SURVEYS_API_URL + '/' + surveyId + '/benchmarks';
export const getGroupBasedLinksUrl = (surveyId: number) =>
	`${Constants.SURVEY_BUILDER_SURVEYS_API_URL}/${surveyId}/groupbasedlinks/download`;

export async function downloadGroupBasedLinks(surveyId: number): Promise<any> {
	const res = await axios.get(getGroupBasedLinksUrl(surveyId), {
		responseType: Constants.FileDownloadResponseType,
		timeout: Constants.FileDownloadTimeout,
		headers: Constants.excelResponseHeader,
	});
	return res.data;
}

export async function getSurveyBenchmarks(surveyId: number): Promise<number[]> {
	const res = await axios.get(getSurveyBenchmarksUrl(surveyId));
	return res.data;
}

export async function saveSurveyBenchmarks(surveyId: number, benchmarkIds: number[]): Promise<number[]> {
	const res = await axios.put(getSurveyBenchmarksUrl(surveyId), benchmarkIds);
	return res.data;
}

export async function getSurveyPreviewModel(surveyId: number): Promise<ISurveyPreviewModel> {
	const res = await axios.get(Constants.SURVEY_BUILDER_SURVEYS_API_URL + '/' + surveyId + '/previewModel');
	return res.data;
}

export const getUpdateSurveyStatusUrl = (surveyId: number) => Constants.SURVEY_BUILDER_SURVEYS_API_URL + '/' + surveyId;

export const minNUrl = (surveyId: number) => Constants.SURVEY_BUILDER_SURVEYS_API_URL + '/' + surveyId + '/min-n';

export async function getSurveyMinN(surveyId: number): Promise<any> {
	const url = minNUrl(surveyId);
	const result = await axios.get(url, { headers: { Pragma: 'no-cache' } });
	return result.data;
}

export async function saveSurveyMinN(surveyId: number, minN?: number): Promise<IValidationModel<number>> {
	const url = minNUrl(surveyId) + '?minn=' + minN;
	const result = await axios.put(url, { headers: { Pragma: 'no-cache' } });
	return result.data;
}

export async function getAudienceSource(surveyId: number): Promise<IAudienceSourceVm> {
	const res = await axios.get(Constants.SURVEY_BUILDER_SURVEYS_API_URL + '/' + surveyId + '/audience-source');
	return res.data;
}

const extractQuestionsFromSummary = (data: ISummaryItem[]): IQuestion[] => {
	const questionProperties = data[0].value.filter((prop: any) => prop.propertyName === 'Questions');
	return questionProperties[0].value.Questions;
};

const convertSummaryQuestionsToLibraryQuestions = (questions: IQuestion[]): ILibraryQuestion[] => {
	return questions.map((question: any) => ({
		...question,
		sourceType: deriveSourceType(question.ownerId),
		questionVariationId: question.sourceQuestionVariationId,
		questionId: question.surveyQuestionId,
		responseOptions: question.responseOptions && question.responseOptions.map((option: any) => ({ displayText: option })),
		alternateVersions: [],
	}));
};

export const deriveSourceType = (ownerId: number | undefined): SourceType => {
	if (!ownerId) {
		return SourceType.Custom;
	} else if (ownerId === Constants.KINCENTRIC_OWNER_ID) {
		return SourceType.KincentricLibrary;
	}
	return SourceType.ClientLibrary;
};

export async function getSurveyTemplates(): Promise<ISurveyTemplate[]> {
	const res = await axios.get(Constants.API_SURVEY_TEMPLATES);
	return res.data;
}

export async function setTemplateStatus(surveyId: number, status: SurveyStatusType) {
	const res = await axios.put(Constants.API_SURVEY_TEMPLATES + '/' + surveyId + '/set-status?status=' + status);
	return res.data;
}

export async function copySurveyTemplate(surveyId: number, clientId: number) {
	const res = await axios.post(Constants.API_SURVEY_TEMPLATES + '/' + surveyId + '/clone/' + clientId);
	return res.data;
}

export async function getSurveyBridge(surveyId: number): Promise<ISurveyBridge> {
	const res = await axios.get(Constants.API_SURVEY_BRIDGE.replace('{surveyId}', surveyId.toString()));
	return res.data;
}

export const getSurveyStatusLogs = async (surveyId: number): Promise<ISurveyStatusLog[]> => {
	const response = await axios.get<ISurveyStatusLog[]>(Constants.API_SURVEY_STATUS_LOGS_URL.replace('{surveyId}', surveyId.toString()));
	return response.data;
};
